import {
  ArrowLeftCircleIcon,
  CheckCircleIcon,
  EllipsisHorizontalCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import {
  Timeline,
  TimelineConnector,
  TimelineHeader,
  TimelineIcon,
  TimelineItem,
  Typography,
} from "@material-tailwind/react";
import classNames from "classnames";
import { Order } from "../../types";
import { formatDateShort, formatTimeShort } from "../lib/formatters";

interface Props {
  order: Order;
}

const getTimelineItemIcon = (
  eventKey: string,
  completed: boolean,
  active: boolean
) => {
  let className = "h-5 w-5";

  switch (eventKey) {
    case "ORDER_VOIDED":
      className = className.concat(" text-red-700");

      return <ExclamationCircleIcon className={className} />;

    case "ORDER_RETURNED":
      className = className.concat(" text-purple-700");

      return <ArrowLeftCircleIcon className={className} />;

    case "DELIVERY_FAILED":
      className = className.concat(" text-red-700");

      return <XCircleIcon className={className} />;

    default:
      if (!completed) {
        className = className.concat(" text-gray-700");
      }

      if (active) {
        className = className.concat(" animate-ping");
      }

      if (completed) {
        return <CheckCircleIcon className={className} />;
      } else {
        return <EllipsisHorizontalCircleIcon className={className} />;
      }
  }
};

const OrderTimeline = ({ order }: Props) => {
  const TIMELINE = order.timeline;
  const totalNumberOfDeliveries = order.deliveries.length;

  return (
    <div className="w-full">
      <Timeline>
      {totalNumberOfDeliveries > 1 && 
        <TimelineItem className="h-8">
            <TimelineHeader className="relative rounded-xl border border-gray-300 font-semibold bg-amber-200 justify-center pl-4 pr-8 shadow-lg shadow-blue-gray-900/5">
              {`${totalNumberOfDeliveries} Couriers`}
            </TimelineHeader>
        </TimelineItem>}
        {TIMELINE.map((orderEvent, ix) => (
          <TimelineItem
            className={classNames(
              "h-24",
              orderEvent.timestamp || orderEvent.description
                ? "opacity-100"
                : "opacity-70"
            )}
            key={ix}
          >
            {ix !== TIMELINE.length - 1 && (
              <TimelineConnector style={{ width: "78px" }} />
            )}

            <TimelineHeader className="relative rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-8 shadow-lg shadow-blue-gray-900/5">
              <TimelineIcon className="p-3" variant="ghost">
                {getTimelineItemIcon(
                  orderEvent.eventKey,
                  !!orderEvent.timestamp,
                  !!orderEvent.description
                )}
              </TimelineIcon>
              <div className="flex flex-col gap-1">
                <Typography
                  variant="h6"
                  color={
                    orderEvent.timestamp || orderEvent.description
                      ? "blue-gray"
                      : "gray"
                  }
                  className={orderEvent.description && !orderEvent.timestamp ? "animate-pulse" : ""}
                >
                  {orderEvent.label}
                </Typography>
                {orderEvent.description && <Typography
                  variant="small"
                  color="gray"
                  className={
                    orderEvent.description && !orderEvent.timestamp
                      ? "font-normal italic animate-pulse"
                      : "font-normal italic"
                  }
                >
                  {orderEvent.description}
                </Typography>}
                {orderEvent.timestamp && <Typography
                  variant="small"
                  color="gray"
                  className={
                    orderEvent.description && !orderEvent.timestamp
                      ? "font-normal italic animate-pulse"
                      : "font-normal italic"
                  }
                >
                  {`at ${formatTimeShort(
                        orderEvent.timestamp
                      )} on ${formatDateShort(orderEvent.timestamp)}`}
                </Typography>}
              </div>
            </TimelineHeader>
          </TimelineItem>
        ))}
      </Timeline>
    </div>
  );
};

export default OrderTimeline;
